import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Search,
  X,
  Compass,
  Package,
  FlaskConical,
  ClipboardList,
  Building2,
  FileText,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { ALL_REGISTRY_ENTRIES } from '../constants/modules';
import { usePermissions } from '../context/PermissionContext';
import { useDocumentTypesContext } from '../context/DocumentTypesContext';
import { getDocumentPath } from '../routes/documentPath';

const apiUrl = process.env.REACT_APP_API_URL ?? '';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchItemResult {
  _id: string;
  code: string;
  name: string;
  barcode?: string;
}

interface SearchPartnerResult {
  _id: string;
  partner: string;
  name: string;
  taxNumber?: string;
  city?: string;
}

interface SearchNormativResult {
  _id: string;
  batch?: number;
  items?: {
    _id: string;
    code: string;
    name: string;
  };
}

interface SearchWorkOrderResult {
  _id: string;
  number: string;
  documentCode?: string;
  status?: string;
  items?: {
    _id: string;
    code: string;
    name: string;
  };
}

interface SearchDocumentResult {
  _id: string;
  number: string;
  date?: string;
  documentType?: {
    _id?: string;
    code: string;
    name: string;
  };
  partner?: {
    name: string;
  };
}

interface FlatSearchResult {
  category: 'screen' | 'item' | 'normativ' | 'workOrder' | 'partner' | 'document';
  id: string;
  title: string;
  subtitle: string;
  tag: string;
  onSelect: () => void;
}

const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasAccess, isAdmin } = usePermissions();
  const { allDocumentTypes } = useDocumentTypesContext();

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Server results
  const [serverResults, setServerResults] = useState<{
    items: SearchItemResult[];
    partners: SearchPartnerResult[];
    normativs: SearchNormativResult[];
    workOrders: SearchWorkOrderResult[];
    documents: SearchDocumentResult[];
  }>({
    items: [],
    partners: [],
    normativs: [],
    workOrders: [],
    documents: [],
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Auto-focus when modal opens and clear query
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setServerResults({
        items: [],
        partners: [],
        normativs: [],
        workOrders: [],
        documents: [],
      });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Client-side accessible screens matching the query
  const matchingScreens = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ALL_REGISTRY_ENTRIES.filter((entry) => {
      if (!entry.path) return false;
      if (entry.adminOnly && !isAdmin) return false;
      if (!hasAccess(entry.key)) return false;

      if (!q) return false;
      const label = t(entry.labelKey).toLowerCase();
      const path = entry.path.toLowerCase();
      return label.includes(q) || path.includes(q);
    }).slice(0, 5);
  }, [query, t, hasAccess, isAdmin]);

  // Default quick screens when query is empty
  const defaultScreens = useMemo(() => {
    return ALL_REGISTRY_ENTRIES.filter((entry) => {
      if (!entry.path) return false;
      if (entry.adminOnly && !isAdmin) return false;
      if (!hasAccess(entry.key)) return false;
      return [
        '/work-orders',
        '/work-order/6020',
        '/os',
        '/items',
        '/partners',
        '/stock-overview',
        '/warehouse-card',
      ].includes(entry.path);
    }).slice(0, 5);
  }, [hasAccess, isAdmin]);

  // Debounced API search
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setServerResults({
        items: [],
        partners: [],
        normativs: [],
        workOrders: [],
        documents: [],
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${apiUrl}/api/global-search`, {
          params: { q: trimmed },
          headers: { 'x-auth-token': token || '' },
        });
        setServerResults(res.data);
      } catch (err) {
        console.error('Failed to execute global search:', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  // Flattened results list for easy arrow key navigation
  const flatResults = useMemo<FlatSearchResult[]>(() => {
    const list: FlatSearchResult[] = [];

    // If query is empty, show default screens
    if (!query.trim()) {
      defaultScreens.forEach((entry) => {
        list.push({
          category: 'screen',
          id: `screen-${entry.path}`,
          title: t(entry.labelKey),
          subtitle: entry.path || '',
          tag: t('sidebar.menu', 'Мени'),
          onSelect: () => {
            if (entry.path) navigate(entry.path);
            onClose();
          },
        });
      });
      return list;
    }

    // 1. Screens
    matchingScreens.forEach((entry) => {
      list.push({
        category: 'screen',
        id: `screen-${entry.path}`,
        title: t(entry.labelKey),
        subtitle: entry.path || '',
        tag: t('sidebar.menu', 'Екран'),
        onSelect: () => {
          if (entry.path) navigate(entry.path);
          onClose();
        },
      });
    });

    // 2. Items
    serverResults.items.forEach((item) => {
      list.push({
        category: 'item',
        id: `item-${item._id}`,
        title: item.name || item.code,
        subtitle: `Шифра: ${item.code}${item.barcode ? ` · Баркод: ${item.barcode}` : ''}`,
        tag: 'Идент',
        onSelect: () => {
          navigate('/items', { state: { searchCode: item.code } });
          onClose();
        },
      });
    });

    // 3. Normativs / OS
    serverResults.normativs.forEach((normativ) => {
      const prodName = normativ.items?.name || normativ.items?.code || 'Норматив';
      list.push({
        category: 'normativ',
        id: `normativ-${normativ._id}`,
        title: prodName,
        subtitle: normativ.items?.code ? `За идент: ${normativ.items.code}` : 'Основна спецификација',
        tag: 'Норматив',
        onSelect: () => {
          navigate('/os', { state: { selectedIngredientId: normativ._id } });
          onClose();
        },
      });
    });

    // 4. Work Orders
    serverResults.workOrders.forEach((wo) => {
      const prodName = wo.items?.name ? ` · ${wo.items.name}` : '';
      list.push({
        category: 'workOrder',
        id: `wo-${wo._id}`,
        title: `Налог бр. ${wo.number}${prodName}`,
        subtitle: wo.status ? `Статус: ${wo.status}` : 'Работен налог 6020',
        tag: 'РН 6020',
        onSelect: () => {
          navigate('/work-order/6020', { state: { workOrderId: wo._id } });
          onClose();
        },
      });
    });

    // 5. Partners
    serverResults.partners.forEach((partner) => {
      list.push({
        category: 'partner',
        id: `partner-${partner._id}`,
        title: partner.name,
        subtitle: `Шифра: ${partner.partner}${partner.taxNumber ? ` · ЕДБ: ${partner.taxNumber}` : ''}${partner.city ? ` · ${partner.city}` : ''}`,
        tag: 'Партнер',
        onSelect: () => {
          navigate('/partners', { state: { searchCode: partner.partner } });
          onClose();
        },
      });
    });

    // 6. Documents
    serverResults.documents.forEach((doc) => {
      const type = allDocumentTypes.find(candidate => candidate._id === doc.documentType?._id);
      if (!type) return;
      const typeLabel = doc.documentType?.name || doc.documentType?.code || 'Документ';
      list.push({
        category: 'document',
        id: `doc-${doc._id}`,
        title: `${typeLabel} — ${doc.number}`,
        subtitle: doc.partner?.name ? `Партнер: ${doc.partner.name}` : 'Материјален документ',
        tag: doc.documentType?.code || 'Документ',
        onSelect: () => {
          navigate(getDocumentPath(type), { state: { documentNumber: doc.number } });
          onClose();
        },
      });
    });

    return list;
  }, [query, matchingScreens, defaultScreens, serverResults, allDocumentTypes, t, navigate, onClose]);

  // Keep selected index in bounds
  useEffect(() => {
    setSelectedIndex((prev) => {
      if (flatResults.length === 0) return 0;
      if (prev >= flatResults.length) return flatResults.length - 1;
      return prev;
    });
  }, [flatResults.length]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (flatResults.length === 0 ? 0 : (prev + 1) % flatResults.length));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (flatResults.length === 0 ? 0 : (prev - 1 + flatResults.length) % flatResults.length));
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const current = flatResults[selectedIndex];
      if (current) {
        current.onSelect();
      }
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.querySelector(`[data-result-index="${selectedIndex}"]`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  const getIconForCategory = (category: FlatSearchResult['category']) => {
    switch (category) {
      case 'screen':
        return <Compass size={17} className="text-blue-500" />;
      case 'item':
        return <Package size={17} className="text-emerald-500" />;
      case 'normativ':
        return <FlaskConical size={17} className="text-purple-500" />;
      case 'workOrder':
        return <ClipboardList size={17} className="text-amber-500" />;
      case 'partner':
        return <Building2 size={17} className="text-indigo-500" />;
      case 'document':
        return <FileText size={17} className="text-teal-500" />;
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10005] flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm transition-opacity"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col max-h-[75vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input Bar */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
          <Search size={20} className="text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Пребарај екрани, иденти, нормативи, налози, партнери..."
            className="w-full bg-transparent text-sm sm:text-base text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none"
          />
          {loading && <Loader2 size={18} className="animate-spin text-blue-500 mr-2 flex-shrink-0" />}
          {query && (
            <button
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md transition-colors"
            >
              <X size={16} />
            </button>
          )}
          <span className="hidden sm:inline-block ml-2 px-1.5 py-0.5 text-[10px] font-semibold text-gray-400 border border-gray-300 dark:border-gray-600 rounded">
            ESC
          </span>
        </div>

        {/* Results List */}
        <div ref={listRef} className="overflow-y-auto flex-1 divide-y divide-gray-100 dark:divide-gray-700/50">
          {!query.trim() && (
            <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50/80 dark:bg-gray-800/80">
              Често користени екрани
            </div>
          )}

          {flatResults.length === 0 && !loading && query.trim().length >= 2 && (
            <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              Нема пронајдено резултати за „<span className="font-semibold">{query}</span>“
            </div>
          )}

          {flatResults.map((result, idx) => {
            const isSelected = idx === selectedIndex;
            return (
              <div
                key={result.id}
                data-result-index={idx}
                onClick={() => result.onSelect()}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-blue-50 border-l-4 border-blue-500 dark:bg-blue-900/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 border-transparent'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-md flex-shrink-0">
                    {getIconForCategory(result.category)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                      {result.title}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {result.subtitle}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {result.tag}
                  </span>
                  {isSelected && <ArrowRight size={14} className="text-blue-500" />}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer shortcuts helper */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="px-1 py-0.5 font-sans font-semibold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-[10px]">
                ↑
              </kbd>{' '}
              <kbd className="px-1 py-0.5 font-sans font-semibold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-[10px]">
                ↓
              </kbd>{' '}
              за навигација
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 font-sans font-semibold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-[10px]">
                ↵
              </kbd>{' '}
              за избор
            </span>
          </div>
          <div>
            <kbd className="px-1.5 py-0.5 font-sans font-semibold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-[10px]">
              ESC
            </kbd>{' '}
            за затворање
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalSearchModal;
